import {
    GOLDILOCKS,
    LEVEL_FIVE,
    LEVEL_FOUR,
    LEVEL_ONE,
    LEVEL_THREE,
    LEVEL_TWO,
    NONE,
    UNRECOGNIZED,
} from "../components/UserVisibleStrings";
import { RiskCategory, WeatherRiskConfig } from "../generated/gpb/api/risk";
import { assertUnreachable } from "./utils";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
    faFaceAngry,
    faFaceAnxiousSweat,
    faFaceFrownOpen,
    faFaceHandPeeking,
    faFaceMeh,
    faFaceSmile,
    faFaceSmileHearts,
    IconDefinition
} from "@fortawesome/sharp-duotone-light-svg-icons";
import Fraction from "fraction.js";

// Helper function to check for valid values (not undefined, null, or negative infinity)
const isValid = ( value: number | undefined ) => value !== undefined && value !== null && value !== -Infinity;
function getValidString( valueToEvaluate: number | undefined, stringIfValid: string )
{
    return isValid( valueToEvaluate ) ? stringIfValid : "";
}

export const setRiskConfig = ( data : WeatherRiskConfig | undefined,
    error : FetchBaseQueryError | SerializedError | undefined,
    isLoading : boolean ) =>
{
    if ( error )
    {
        console.error( "Error:", error );
    }

    if ( data !== undefined && !isLoading )
    {

        // Update riskLevelInfo with fetched data
        impactLevelInfo[GOLDILOCKS] = {
            ...impactLevelInfo[GOLDILOCKS],
            windDetails: getValidString( data.goldilocksConfig?.windGustConfigMPH, `Wind gusts below ${data.goldilocksConfig?.windGustConfigMPH} miles per hour.` ),
            tempDetails: ( isValid( data.goldilocksConfig?.apparentTemperatureConfigF?.lowerBound ) && isValid( data.goldilocksConfig?.apparentTemperatureConfigF?.upperBound ) )
                ? `Feels like temperatures greater than ${data.goldilocksConfig?.apparentTemperatureConfigF?.lowerBound}°F or less than ${data.goldilocksConfig?.apparentTemperatureConfigF?.upperBound}°F.`
                : isValid( data.goldilocksConfig?.apparentTemperatureConfigF?.lowerBound )
                    ? `Feels like temperatures greater than ${data.goldilocksConfig?.apparentTemperatureConfigF?.lowerBound}°F.`
                    : isValid( data.goldilocksConfig?.apparentTemperatureConfigF?.upperBound )
                        ? `Feels like temperatures less than ${data.goldilocksConfig?.apparentTemperatureConfigF?.upperBound}°F.`
                        : "",
            cloudCoverDetails: getValidString( data.goldilocksConfig?.skyCoverConfigPERCENT, `Less than ${data.goldilocksConfig?.skyCoverConfigPERCENT} percent cloud cover.` ),
            visibilityDetails: getValidString( data.goldilocksConfig?.visibilityConfigMILES, `Visibility greater than ${formatVisibility( data.goldilocksConfig?.visibilityConfigMILES )}` ),
            dewpointDetails: getValidString( data.goldilocksConfig?.dewPointConfigF, `Dewpoint is less than ${data.goldilocksConfig?.dewPointConfigF}°F` )
        };

        impactLevelInfo[NONE] = {
            ...impactLevelInfo[NONE],
            windDetails: getValidString( data.windGustRiskConfigMPH?.lowRiskValue, `Wind gusts below ${data.windGustRiskConfigMPH?.lowRiskValue} miles per hour.` ),
            rainDetails: "Light rain, rain showers, or drizzle.  Barely wets the pavement.",
            snowDetails: "No more than snow flurries",
            iceDetails: "No ice accumulation",
            tempDetails: "Feels like temperatures between 0°F and 100°F.",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.lowRiskValue, `Visibility greater than ${formatVisibility( data.visibilityRiskConfigMILES?.lowRiskValue )}` )
        };

        impactLevelInfo[LEVEL_ONE] = {
            ...impactLevelInfo[LEVEL_ONE],
            windDetails: getValidString( data.windGustRiskConfigMPH?.lowRiskValue, `Wind gusts between ${data.windGustRiskConfigMPH?.lowRiskValue}-${data.windGustRiskConfigMPH?.lowMediumRiskValue} miles per hour.` ),
            rainDetails: getValidString( data.rainAccumulationRiskConfig?.lowRiskValue, `Rain rate at least ${data.rainAccumulationRiskConfig?.lowRiskValue} inches per hour.` ),
            snowDetails: getValidString( data.snowAccumulationRiskConfig?.lowRiskValue, `Snowfall rate at least ${data.snowAccumulationRiskConfig?.lowRiskValue} inches per hour.` ),
            iceDetails: getValidString( data.iceAccumulationRiskConfig?.lowRiskValue, `Ice accumulation rate at least ${data.iceAccumulationRiskConfig?.lowRiskValue} inches per hour.` ),
            tempDetails: ( isValid( data.apparentTemperatureRiskConfigF?.lowRiskValue?.lowerBound ) && isValid( data.apparentTemperatureRiskConfigF?.lowRiskValue?.upperBound ) )
                ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.lowRiskValue?.lowerBound}°F or greater than ${data.apparentTemperatureRiskConfigF?.lowRiskValue?.upperBound}°F.`
                : isValid( data.apparentTemperatureRiskConfigF?.lowRiskValue?.lowerBound )
                    ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.lowRiskValue?.lowerBound}°F.`
                    : isValid( data.apparentTemperatureRiskConfigF?.lowRiskValue?.upperBound )
                        ? `Feels like temperatures greater than ${data.apparentTemperatureRiskConfigF?.lowRiskValue?.upperBound}°F.`
                        : "",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.lowRiskValue, `Visibility less than ${formatVisibility( data.visibilityRiskConfigMILES?.lowRiskValue )}` )
        };

        impactLevelInfo[LEVEL_TWO] = {
            ...impactLevelInfo[LEVEL_TWO],
            windDetails: getValidString( data.windGustRiskConfigMPH?.lowMediumRiskValue, `Wind gusts between ${data.windGustRiskConfigMPH?.lowMediumRiskValue}-${data.windGustRiskConfigMPH?.mediumRiskValue} miles per hour.` ),
            rainDetails: getValidString( data.rainAccumulationRiskConfig?.lowMediumRiskValue, `Rain rate at least ${data.rainAccumulationRiskConfig?.lowMediumRiskValue} inches per hour.` ),
            snowDetails: getValidString( data.snowAccumulationRiskConfig?.lowMediumRiskValue, `Snowfall rate at least ${data.snowAccumulationRiskConfig?.lowMediumRiskValue} inches per hour.` ),
            iceDetails: getValidString( data.iceAccumulationRiskConfig?.lowMediumRiskValue, `Ice accumulation rate at least ${data.iceAccumulationRiskConfig?.lowMediumRiskValue} inches per hour.` ),
            tempDetails: ( isValid( data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.lowerBound ) && isValid( data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.upperBound ) )
                ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.lowerBound}°F or greater than ${data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.upperBound}°F.`
                : isValid( data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.lowerBound )
                    ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.lowerBound}°F.`
                    : isValid( data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.upperBound )
                        ? `Feels like temperatures greater than ${data.apparentTemperatureRiskConfigF?.lowMediumRiskValue?.upperBound}°F.`
                        : "",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.lowMediumRiskValue, `Visibility less than ${formatVisibility( data.visibilityRiskConfigMILES?.lowMediumRiskValue )}` )
        };

        impactLevelInfo[LEVEL_THREE] = {
            ...impactLevelInfo[LEVEL_THREE],
            windDetails: getValidString( data.windGustRiskConfigMPH?.mediumRiskValue, `Wind gusts between ${data.windGustRiskConfigMPH?.mediumRiskValue}-${data.windGustRiskConfigMPH?.mediumHighRiskValue} miles per hour.` ),
            rainDetails: getValidString( data.rainAccumulationRiskConfig?.mediumRiskValue, `Rain rate at least ${data.rainAccumulationRiskConfig?.mediumRiskValue} inches per hour.` ),
            snowDetails: getValidString( data.snowAccumulationRiskConfig?.mediumRiskValue, `Snowfall rate at least ${data.snowAccumulationRiskConfig?.mediumRiskValue} inches per hour.` ),
            iceDetails: getValidString( data.iceAccumulationRiskConfig?.mediumRiskValue, `Ice accumulation rate at least ${data.iceAccumulationRiskConfig?.mediumRiskValue} inches per hour.` ),
            tempDetails: ( isValid( data.apparentTemperatureRiskConfigF?.mediumRiskValue?.lowerBound ) && isValid( data.apparentTemperatureRiskConfigF?.mediumRiskValue?.upperBound ) )
                ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.mediumRiskValue?.lowerBound}°F or greater than ${data.apparentTemperatureRiskConfigF?.mediumRiskValue?.upperBound}°F.`
                : isValid( data.apparentTemperatureRiskConfigF?.mediumRiskValue?.lowerBound )
                    ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.mediumRiskValue?.lowerBound}°F.`
                    : isValid( data.apparentTemperatureRiskConfigF?.mediumRiskValue?.upperBound )
                        ? `Feels like temperatures greater than ${data.apparentTemperatureRiskConfigF?.mediumRiskValue?.upperBound}°F.`
                        : "",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.mediumRiskValue, `Visibility less than ${formatVisibility( data.visibilityRiskConfigMILES?.mediumRiskValue )}` )
        };

        impactLevelInfo[LEVEL_FOUR] = {
            ...impactLevelInfo[LEVEL_FOUR],
            windDetails: getValidString( data.windGustRiskConfigMPH?.mediumHighRiskValue, `Wind gusts between ${data.windGustRiskConfigMPH?.mediumHighRiskValue}-${data.windGustRiskConfigMPH?.highRiskValue} miles per hour.` ),
            rainDetails: getValidString( data.rainAccumulationRiskConfig?.mediumHighRiskValue, `Rain rate at least ${data.rainAccumulationRiskConfig?.mediumHighRiskValue} inches per hour.` ),
            snowDetails: getValidString( data.snowAccumulationRiskConfig?.mediumHighRiskValue, `Snowfall rate at least ${data.snowAccumulationRiskConfig?.mediumHighRiskValue} inches per hour.` ),
            iceDetails: getValidString( data.iceAccumulationRiskConfig?.mediumHighRiskValue, `Ice accumulation rate at least ${data.iceAccumulationRiskConfig?.mediumHighRiskValue} inches per hour.` ),
            tempDetails: ( isValid( data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.lowerBound ) && isValid( data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.upperBound ) )
                ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.lowerBound}°F or greater than ${data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.upperBound}°F.`
                : isValid( data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.lowerBound )
                    ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.lowerBound}°F.`
                    : isValid( data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.upperBound )
                        ? `Feels like temperatures greater than ${data.apparentTemperatureRiskConfigF?.mediumHighRiskValue?.upperBound}°F.`
                        : "",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.mediumHighRiskValue, `Visibility less than ${formatVisibility( data.visibilityRiskConfigMILES?.mediumHighRiskValue )}` )
        };

        impactLevelInfo[LEVEL_FIVE] = {
            ...impactLevelInfo[LEVEL_FIVE],
            windDetails: getValidString( data.windGustRiskConfigMPH?.highRiskValue, `Wind gusts at hurricane force, greater than ${data.windGustRiskConfigMPH?.highRiskValue} miles per hour.` ),
            rainDetails: getValidString( data.rainAccumulationRiskConfig?.highRiskValue, `Rain rate at least ${data.rainAccumulationRiskConfig?.highRiskValue} inches per hour.` ),
            snowDetails: getValidString( data.snowAccumulationRiskConfig?.highRiskValue, `Snowfall rate at least ${data.snowAccumulationRiskConfig?.highRiskValue} inches per hour.` ),
            iceDetails: getValidString( data.iceAccumulationRiskConfig?.highRiskValue, `Ice accumulation rate at least ${data.iceAccumulationRiskConfig?.highRiskValue} inches per hour.` ),
            tempDetails: ( isValid( data.apparentTemperatureRiskConfigF?.highRiskValue?.lowerBound ) && isValid( data.apparentTemperatureRiskConfigF?.highRiskValue?.upperBound ) )
                ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.highRiskValue?.lowerBound}°F or greater than ${data.apparentTemperatureRiskConfigF?.highRiskValue?.upperBound}°F.`
                : isValid( data.apparentTemperatureRiskConfigF?.highRiskValue?.lowerBound )
                    ? `Feels like temperatures less than ${data.apparentTemperatureRiskConfigF?.highRiskValue?.lowerBound}°F.`
                    : isValid( data.apparentTemperatureRiskConfigF?.highRiskValue?.upperBound )
                        ? `Feels like temperatures greater than ${data.apparentTemperatureRiskConfigF?.highRiskValue?.upperBound}°F.`
                        : "",
            visibilityDetails: getValidString( data.visibilityRiskConfigMILES?.highRiskValue, `Visibility less than ${formatVisibility( data.visibilityRiskConfigMILES?.highRiskValue )}` )
        };

        return data;
    }
};



export const riskLevels = [
    UNRECOGNIZED,
    GOLDILOCKS,
    NONE,
    LEVEL_ONE,
    LEVEL_TWO,
    LEVEL_THREE,
    LEVEL_FOUR,
    LEVEL_FIVE,
] as const;
export const pagesToShow = riskLevels.filter( each => each !== UNRECOGNIZED );
type RiskLevelDetails = {
    tooltip: string,
    windDetails: string | undefined,
    rainDetails: string | undefined,
    snowDetails: string | undefined,
    iceDetails: string | undefined,
    tempDetails: string | undefined,
    thunderstormDetails: string,
    cloudCoverDetails: string | undefined,
    visibilityDetails: string | undefined,
    dewpointDetails: string | undefined,
    color: string,
    category: RiskCategory,
    shortLabel: string,
    icon: IconDefinition,
};
const defaultSettings = {
    windDetails: undefined,
    tempDetails: undefined,
    cloudCoverDetails: undefined,
    visibilityDetails: undefined,
    dewpointDetails: undefined,
    rainDetails: undefined,
    snowDetails: undefined,
    iceDetails: undefined,
};
export const impactLevelInfo: { [key: string]: RiskLevelDetails } = {};
impactLevelInfo[NONE] = { ...defaultSettings,
    tooltip: NONE + ": No weather impacts for RV travelers",
    thunderstormDetails: "Very unlikely",
    color: "#FFFFFF",
    category: RiskCategory.RISK_CATEGORY_NONE,
    icon: faFaceSmile,
    shortLabel: "0" };
//Update riskLevelInfo with fetched data
impactLevelInfo[GOLDILOCKS] = { ...defaultSettings,
    tooltip: GOLDILOCKS + " Weather - Weather is ideal for RV'ers",
    rainDetails: "No rain.",
    snowDetails: "No snow.",
    iceDetails: "No measurable ice accumulation.",
    thunderstormDetails: "No thunderstorm risk.",
    color: "#B2EBF2",
    category: RiskCategory.RISK_CATEGORY_GOLDILOCKS,
    icon: faFaceSmileHearts,
    shortLabel: "G", };
impactLevelInfo[LEVEL_ONE] = { ...defaultSettings,
    tooltip: LEVEL_ONE + ": Light weather impacts for RV'ers",
    thunderstormDetails: "Thunderstorms, some of which may have strong wind gusts and small hail.",
    color: "#FFF59D",
    category: RiskCategory.RISK_CATEGORY_LOW,
    icon: faFaceMeh,
    shortLabel: "1" };
impactLevelInfo[LEVEL_TWO] = { ...defaultSettings,
    tooltip: LEVEL_TWO + ": More noticeable weather impacts to RV'ers",
    thunderstormDetails: "Strong to marginally severe thunderstorms possible.  Storms may contain gusty winds and hail.",
    color: "#FFC107",
    category: RiskCategory.RISK_CATEGORY_LOW_MEDIUM,
    icon: faFaceFrownOpen,
    shortLabel: "2" };
impactLevelInfo[LEVEL_THREE] = { ...defaultSettings,
    tooltip: LEVEL_THREE + ": Quite a few RV'ers will want to avoid this type of weather when on the road.",
    thunderstormDetails: "Strong to severe thunderstorms expected.  Hail, damaging wind gusts and a couple of tornadoes are possible.",
    color: "#F57C00",
    category: RiskCategory.RISK_CATEGORY_MEDIUM,
    icon: faFaceAngry,
    shortLabel: "3" };
impactLevelInfo[LEVEL_FOUR] = { ...defaultSettings,
    tooltip: LEVEL_FOUR + ": Most RV'ers will want to seek shelter off the road.  Difficult driving conditions for all vehicles.",
    thunderstormDetails: "Severe thunderstorms, some of which may be intense.  Large hail, damaging wind gusts, and strong tornadoes possible.",
    color: "#E91E63",
    category: RiskCategory.RISK_CATEGORY_MEDIUM_HIGH,
    icon: faFaceAnxiousSweat,
    shortLabel: "4" };
impactLevelInfo[LEVEL_FIVE] = { ...defaultSettings,
    tooltip: LEVEL_FIVE + ": Dangerous weather for RV'ers on the road.  Hazardous driving conditions for all vehicles.",
    thunderstormDetails: "Intense severe thunderstorms which may contain life-threatening tornadoes, large hail and severe wind gusts.",
    color: "#880E4F",
    category: RiskCategory.RISK_CATEGORY_HIGH,
    icon: faFaceHandPeeking,
    shortLabel: "5" };
export type RiskLevel = ( typeof riskLevels )[number];


export function categoryToRiskLevel( category: RiskCategory ): RiskLevel
{
    switch ( category )
    {
        case RiskCategory.RISK_CATEGORY_GOLDILOCKS:
            return GOLDILOCKS;
        case RiskCategory.RISK_CATEGORY_LOW:
            return LEVEL_ONE;
        case RiskCategory.RISK_CATEGORY_LOW_MEDIUM:
            return LEVEL_TWO;
        case RiskCategory.RISK_CATEGORY_MEDIUM:
            return LEVEL_THREE;
        case RiskCategory.RISK_CATEGORY_MEDIUM_HIGH:
            return LEVEL_FOUR;
        case RiskCategory.RISK_CATEGORY_HIGH:
            return LEVEL_FIVE;
        case RiskCategory.RISK_CATEGORY_NONE:
            return NONE;
        case RiskCategory.RISK_CATEGORY_UNSPECIFIED:
        case RiskCategory.UNRECOGNIZED:
            return UNRECOGNIZED;
        default:
            assertUnreachable();
    }
}

export function getEnumFromOrdinal<T extends { [key: string]: string | number }>(
    enumType: T,
    ordinal: number
): T[keyof T] | undefined
{
    const keys = Object.keys( enumType ).filter( ( key ) => isNaN( Number( key ) ) ) as Array<keyof T>;
    if ( ordinal >= 0 && ordinal < keys.length )
    {
        const key = keys[ordinal];
        return enumType[key];
    }
    return undefined;
}

function formatVisibility( visibilityValue: number | undefined )
{
    let formattedVisibility;
    if ( visibilityValue === undefined )
    {
        return "";
    }

    if ( visibilityValue === 1 )
    {
        formattedVisibility = "1 mile";
    }
    else if ( visibilityValue < 1 )
    {
        formattedVisibility = formatFraction( visibilityValue ) + " mile";
    }
    else
    {
        formattedVisibility = `${visibilityValue} miles`;
    }

    return formattedVisibility;
}

function formatFraction( value: number )
{
    const fraction = new Fraction( value );
    return fraction.toFraction();
}

